/**
 * Owner: Masa Bayu
 * File: PwaInstallPanel.tsx
 * Date:Time: 2026-09-16 18:41
 * Description: T8-E1 PWA install UI — shell install/SW; ≠ OfflinePackage · ≠ unlock.
 */

import { useId, useState } from 'react';
import {
  acceptDemoPwaInstall,
  createDemoPwaInstall,
  enableDemoProductNotifications,
  getDemoPwaInstall,
  getDemoPwaProjection,
  getDemoPwaStatus,
  registerDemoPwaShellWorker,
  resetDemoPwaStore,
} from './demo-pwa-store';

export interface PwaInstallPanelProps {
  readonly onNavigate: (path: string) => void;
}

export function PwaInstallPanel({ onNavigate }: PwaInstallPanelProps) {
  const statusId = useId();
  const controlsId = useId();
  const [status, setStatus] = useState(() => getDemoPwaStatus());
  const [install, setInstall] = useState(() => getDemoPwaInstall());
  const [projection, setProjection] = useState(() => getDemoPwaProjection());

  const refresh = (next: string) => {
    setStatus(next);
    setInstall(getDemoPwaInstall());
    setProjection(getDemoPwaProjection());
  };

  return (
    <section className="route-panel pwa-install" aria-labelledby="pwa-install-title">
      <h2 id="pwa-install-title">PWA · install</h2>
      <p>
        Installable shell and notification preferences. Install does not unlock content. Shell
        service worker is not an entitlement grant store. Offline packages remain T8-E2.
      </p>

      <p id={statusId} role="status" aria-live="polite" className="pwa-install__status">
        {status}
        {install !== null ? ` · state ${install.installState}` : ''}
        {projection !== null ? ` · prefs ${String(projection.preferences.length)}` : ''}
      </p>

      <div
        id={controlsId}
        role="group"
        aria-label="PWA install actions"
        className="pwa-install__controls"
      >
        <button
          type="button"
          onClick={() => {
            refresh(createDemoPwaInstall());
          }}
        >
          Create install prompt
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(acceptDemoPwaInstall());
          }}
        >
          Accept install
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(registerDemoPwaShellWorker());
          }}
        >
          Register shell SW
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(enableDemoProductNotifications());
          }}
        >
          Enable product notifications
        </button>
        <button
          type="button"
          onClick={() => {
            resetDemoPwaStore();
            refresh(getDemoPwaStatus());
          }}
        >
          Reset demo
        </button>
        <button
          type="button"
          onClick={() => {
            onNavigate('/');
          }}
        >
          Home
        </button>
      </div>

      <ul aria-label="Advanced experience drilldowns" className="pwa-install__drilldowns">
        <li>
          <button
            type="button"
            onClick={() => {
              onNavigate('/packages');
            }}
          >
            Offline packages
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => {
              onNavigate('/sync');
            }}
          >
            Sync queue
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => {
              onNavigate('/media');
            }}
          >
            Media chrome
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => {
              onNavigate('/flip');
            }}
          >
            Flip intents
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => {
              onNavigate('/motion');
            }}
          >
            Motion
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => {
              onNavigate('/adam');
            }}
          >
            ADAM entry
          </button>
        </li>
      </ul>

      {install !== null ? (
        <ul aria-label="PWA install facts" className="pwa-install__facts">
          <li>{`unlocksContent=${String(install.unlocksContent)}`}</li>
          <li>{`grantsEntitlement=${String(install.grantsEntitlement)}`}</li>
          <li>{`preparesOfflinePackage=${String(install.preparesOfflinePackage)}`}</li>
          <li>{`cachesPublicationTruth=${String(install.cachesPublicationTruth)}`}</li>
          <li>{`serviceWorkerRegistered=${String(install.serviceWorkerRegistered)}`}</li>
        </ul>
      ) : null}
    </section>
  );
}
